import React from 'react';
import { am5, am5themes_Moonrise, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { addLabels } from '../shared/add-labels';
import { createBaseLegend } from '../shared/create-base-legend';
import { createBaseTooltips } from '../shared/create-base-tooltips';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  numberFormat: string | null;
  download: boolean;
  tooltipText: string;
  yAxisLabel: string;
  xAxisLabel: string;
  category: string;

  hideSeries: string[];
  hideLegend: boolean;
  rotation: number;
  seriesOrder: (_c: string[]) => string[];
  forceColor: Record<string, string>;
  displayNumbers: boolean;
  stacked: boolean;
  negativeColor: string;
  positiveColor: string;
  horizontal: boolean;
  colorByBar: boolean;
  paletteVersion: 'v1' | 'v2';
  count: types.widgets.CountData | undefined;
  minAxisValue: number | undefined;
};

const Chart = ({
  id,
  data,
  count,
  fullScreen,
  numberFormat,
  download,
  yAxisLabel,
  xAxisLabel,
  category,
  hideSeries,
  hideLegend,
  tooltipText,
  rotation,
  seriesOrder,
  forceColor,
  displayNumbers,
  stacked,
  negativeColor,
  positiveColor,
  horizontal,
  colorByBar,
  paletteVersion,
  minAxisValue,
}: ChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
  const theme = mui.styles.useTheme() as mui.core.Theme;
  const dataLength = Object.keys(data?.[0] ?? {}).length;
  const shouldShowLegend = !hideLegend && dataLength > 1 && (fullScreen || dataLength <= 6);

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    // Set themes
    root.setThemes([am5themes_Moonrise.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: root.verticalLayout,
        maxTooltipDistance: 0,
        paddingLeft: getPaddingLeft(download, yAxisLabel),
        paddingBottom: shouldShowLegend ? 0 : 20,
      })
    );

    // Root settings
    chart.appear(1000, 100);
    if (numberFormat) {
      root.numberFormatter.setAll({
        numberFormat,
        numericFields: ['valueY'],
      });
    }

    // Create elements
    const { xAxis, yAxis } = createAxes({ root, chart, data, category, horizontal, minAxisValue });

    const barAxis = horizontal ? xAxis : yAxis;
    const extraAmount = horizontal ? 0.05 : 0.15;
    barAxis.setAll({
      extraMax: extraAmount,
      extraMin: minAxisValue !== undefined ? 0 : extraAmount,
    });

    createSeries({
      root,
      chart,
      data,
      category,
      xAxisRenderer: xAxis as any,
      yAxisRenderer: yAxis as any,
      tooltipText,
      hideSeries,
      seriesOrder,
      forceColor,
      rotation,
      displayNumbers,
      stacked,
      negativeColor,
      positiveColor,
      horizontal,
      colorByBar,
      paletteVersion,
      count,
      hideGridLines: true,
    });

    if (shouldShowLegend) createBaseLegend({ root, chart, yAxis, centerX: horizontal ? 40 : undefined });
    createBaseTooltips({
      root,
      chart,
      xAxis: xAxis as any,
      yAxis: yAxis as any,
      theme,
      zoomType: horizontal ? 'zoomY' : 'zoomX',
    });
    handleZoom(chart);
    if (download) createDownloadButton(root, category, data);
    addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none', // or "zoomX", "zoomY", etc., depending on your use case
      })
    );

    // Hide the vertical and horizontal reference lines
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);
    // Disable the tooltips on the axes
    (xAxis as any).set('tooltip', null);
    (yAxis as any).set('tooltip', null);

    return () => {
      root.dispose();
    };
  }, [data]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default Chart;
